import { useMutation, useQuery } from "@tanstack/react-query";
import { LoaderCircle, Settings } from "lucide-react";
import { useEffect, useState } from "react";

import { ErrorState, LoadingState, PageHeader, StatusBadge } from "../account/components";
import { api } from "../api";
import { ConfigurationForm } from "../setup/ConfigurationForm";
import { ManagedConfiguration, ManagedConfigurationSnapshot, emptySecrets } from "../setup/configuration";

export function ManagedSettingsPage() {
  const settings = useQuery({
    queryKey: ["managed-settings"],
    queryFn: () => api<ManagedConfigurationSnapshot>("/api/v1/operator/super-admin/settings"),
    retry: false,
  });
  const [configuration, setConfiguration] = useState<ManagedConfiguration | null>(null);
  const [tested, setTested] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    if (settings.data && !configuration) {
      setConfiguration({
        ...settings.data.configuration,
        valkey: {
          ...settings.data.configuration.valkey,
          clearPassword: !settings.data.secrets.valkeyPassword,
        },
      });
    }
  }, [configuration, settings.data]);

  useEffect(() => {
    if (!restarting) return;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const response = await fetch("/api/v1/setup/status", { cache: "no-store", credentials: "include" });
        if (!cancelled && response.ok) window.location.assign("/operator/settings");
      } catch {
        // The process is expected to be briefly unreachable while it restarts.
      }
    }, 1000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [restarting]);

  const test = useMutation({
    mutationFn: () => api<{ postgres: boolean; valkey: boolean }>("/api/v1/operator/super-admin/settings/connection-tests", {
      method: "POST",
      body: JSON.stringify(configuration),
    }),
    onSuccess: () => setTested(true),
  });
  const update = useMutation({
    mutationFn: () => api<{ revision: string }>("/api/v1/operator/super-admin/settings", {
      method: "PUT",
      body: JSON.stringify(configuration),
    }),
    onSuccess: () => setRestarting(true),
  });

  return <>
    <PageHeader
      eyebrow="SUPER ADMIN"
      title="系统设置"
      description="维护托管安装的 PostgreSQL、Redis / Valkey、认证与文档公开入口、邮件和人机验证配置。保存后服务会自动重启。"
      action={<StatusBadge tone="good"><Settings />托管配置</StatusBadge>}
    />
    {settings.isPending && <LoadingState label="正在读取脱敏配置" />}
    {settings.error && <ErrorState error={settings.error} retry={() => void settings.refetch()} />}
    {restarting && <div className="managed-restart" role="status"><LoaderCircle className="spin" /><div><strong>配置已写入，服务正在重启</strong><span>连接恢复后会自动重新载入此页面。</span></div></div>}
    {configuration && !restarting && <ConfigurationForm
      value={configuration}
      secrets={settings.data?.secrets ?? emptySecrets}
      busy={test.isPending || update.isPending}
      error={test.error ?? update.error}
      tested={tested}
      submitLabel="保存并重启"
      onChange={(value) => { setConfiguration(value); setTested(false); }}
      onTest={() => test.mutate()}
      onSubmit={() => update.mutate()}
    />}
  </>;
}
